import {catchError, EMPTY, Observable, tap} from 'rxjs';

import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatStepper, MatStepperModule} from '@angular/material/stepper';

import {ApmTranslatePipe, PermissionUtils} from '@mitsubishi/core';
import {
  ProjectSelectionsFacade,
  SelectionService,
  StatisticData,
  StatisticDataArray,
  StatisticOption,
} from '@mitsubishi/project/data-access';
import {ButtonComponent, IconComponent, SectionTitleComponent, ValuesPipe} from '@mitsubishi/shared/ui';

import {StatisticDataItemComponent} from '../statistics-data-item/statistic-data-item.component';
import {FaIconComponent} from "@fortawesome/angular-fontawesome";

@Component({
  selector: 'apm-statistic-data-dialog',
  templateUrl: './statistic-data-dialog.component.html',
  styleUrls: ['./statistic-data-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatStepperModule,
    StatisticDataItemComponent,
    SectionTitleComponent,
    ApmTranslatePipe,
    ButtonComponent,
    FaIconComponent,
    IconComponent,
    ValuesPipe,
  ],
})
export class StatisticDataDialogComponent {
  @ViewChild(MatStepper) stepper?: MatStepper;

  hasDatasheetMoreDetails = PermissionUtils.hasDatasheetMoreDetails();

  selectedStatistics: Record<string, StatisticOption | null> = {};

  summaryHoverIndex: number | null = null;

  statisticGroupsData: StatisticData[] = [];

  statisticList$: Observable<StatisticData[]> = this.quoteService.getStatisticData().pipe(
    tap((data) => {
      this.statisticGroupsData = data;

      // Build new selectedStatistics object immutably
      this.selectedStatistics = data.reduce((acc, item) => {
        acc[item.groupCode] = (this.selectedData ?? {})[item.groupCode];
        return acc;
      }, {} as Record<string, StatisticOption | null>);

      const firstNullIndex = Object.values(this.selectedStatistics).findIndex((v) => v == null);
      if (firstNullIndex !== -1) {
        this.selectedIndex = firstNullIndex;
      } else {
        this.selectedIndex = data.length;
      }
    }),
    catchError((error) => {
      console.error('Error loading statistic data:', error);
      // Close dialog on error to prevent broken state
      this.dialogRef.close(false);
      return EMPTY;
    })
  );

  selectedIndex: number = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public selectedData: Record<string, StatisticOption>,
    public dialogRef: MatDialogRef<StatisticDataDialogComponent>,
    private quoteService: SelectionService,
    private quoteFacade: ProjectSelectionsFacade,
    private cdr: ChangeDetectorRef
  ) {}

  get allStepsCompleted(): boolean {
    return this.stepper?.steps
      .toArray()
      .slice(0, -1)
      .every(step => step.completed) ?? false;
  }

  get isConfirmDisabled(): boolean {
    return this.stepper?.steps.some(step => !step.completed) ?? true;
  }

  goPrev() {
    this.stepper?.previous();
  }

  goNext() {
    this.stepper?.next();
  }

  onStatisticSelected(statistic: StatisticOption | undefined | null, groupCode: string) {
    if (!statistic) {
      return;
    }
    this.selectedStatistics = {
      ...this.selectedStatistics,
      [groupCode]: statistic
    };

    if (this.stepper && this.stepper.selected) {
      this.stepper.selected.completed = true;
      this.stepper.next();
    }

    // Validate and invalidate subsequent selections if their dependencies are no longer satisfied
    this.validateSubsequentSelections(groupCode);
  }

  confirm() {
    const selectionId = this.quoteFacade.activeSelection?.details.uuid;
    selectionId && this.quoteFacade.setStatisticData(selectionId, this.getStatisticsDataPayload());
    this.dialogRef.close(true);
  }

  skipAnswers(statisticGroup: StatisticDataArray[]) {
    const selectionId = this.quoteFacade.activeSelection?.details.uuid;
    if (!selectionId) {
      return;
    }

    this.selectedStatistics = statisticGroup.reduce(
      (acc, curr) => {
        const previousGroupCode = curr.previousGroupCode;
        const groupStatistics = curr.statistics ?? [];
        if (groupStatistics.length === 0) {
          return acc;
        }

        if (!previousGroupCode) {
          acc[curr.groupCode] = groupStatistics[0];
          return acc;
        }

        const filteredStatistics = groupStatistics.filter(statistic => {
          // OR logic: show if no dependencies or if any dependency matches ANY previous selection
          if (!statistic.requiresAnyOf || statistic.requiresAnyOf.length === 0) {
            return true;
          }

          // Check against ALL previously selected values, not just the immediate previous group
          const selectedValues = Object.values(acc).map(sel => sel.statisticCode);
          return statistic.requiresAnyOf.some(required =>
            selectedValues.includes(required)
          );
        });
        if (filteredStatistics.length === 0) {
          return acc;
        }

        acc[curr.groupCode] = filteredStatistics[0];

        return acc;
      },
      {} as Record<string, StatisticOption>
    );

    this.quoteFacade.setStatisticData(selectionId, this.getStatisticsDataPayload());
    this.dialogRef.close(true);
  }

  private getStatisticsDataPayload(): StatisticDataArray[] {
    return Object.keys(this.selectedStatistics).reduce((acc, curr) => {
      const selectedStatistic = this.selectedStatistics[curr];

      // Only include non-null selections
      if (selectedStatistic) {
        acc.push({
          groupCode: curr,
          statisticCode: selectedStatistic.statisticCode,
        });
      }

      return acc;
    }, [] as StatisticDataArray[]);
  }


  setSelectedStep(i: number) {
    this.selectedIndex = i;
  }

  /**
   * Validates all subsequent selections after the current group and invalidates them
   * if their dependencies (requiresAnyOf) are no longer satisfied.
   */
  private validateSubsequentSelections(currentGroupCode: string): void {
    // Find the index of the current group
    const currentGroupIndex = this.statisticGroupsData.findIndex(
      group => group.groupCode === currentGroupCode
    );

    if (currentGroupIndex === -1) {
      return;
    }

    // Collect all invalidations first, then apply them in a single mutation
    const invalidatedGroups: string[] = [];
    const stepsToMarkIncomplete: number[] = [];

    // Check all subsequent groups
    for (let i = currentGroupIndex + 1; i < this.statisticGroupsData.length; i++) {
      const group = this.statisticGroupsData[i];
      const selectedStatistic = this.selectedStatistics[group.groupCode];

      // If no selection for this group, skip
      if (!selectedStatistic) {
        continue;
      }

      // Check if the selected statistic is still valid
      const isValid = this.isStatisticValid(selectedStatistic, group.groupCode);

      // If not valid, collect the group for invalidation
      if (!isValid) {
        invalidatedGroups.push(group.groupCode);
        stepsToMarkIncomplete.push(i);
      }
    }

    // Apply all invalidations in a single mutation if needed
    if (invalidatedGroups.length > 0) {
      const updates = invalidatedGroups.reduce((acc, groupCode) => {
        acc[groupCode] = null;
        return acc;
      }, {} as Record<string, null>);

      this.selectedStatistics = {
        ...this.selectedStatistics,
        ...updates
      };

      // Mark all affected steps as incomplete
      const steps = this.stepper?.steps.toArray();
      if (steps) {
        stepsToMarkIncomplete.forEach(index => {
          steps[index].completed = false;
        });
      }

      // Trigger change detection since we're using OnPush strategy
      this.cdr.markForCheck();
    }
  }

  /**
   * Checks if a statistic is still valid based on its requiresAnyOf dependencies
   * and the selections made in previous groups.
   */
  private isStatisticValid(statistic: StatisticOption, groupCode: string): boolean {
    // If the statistic has no dependencies, it's always valid
    if (!statistic.requiresAnyOf || statistic.requiresAnyOf.length === 0) {
      return true;
    }

    // Get all selected values BEFORE this group (not including this group itself)
    const currentGroupIndex = this.statisticGroupsData.findIndex(
      group => group.groupCode === groupCode
    );

    const previousSelections: string[] = [];
    for (let i = 0; i < currentGroupIndex; i++) {
      const group = this.statisticGroupsData[i];
      const selected = this.selectedStatistics[group.groupCode];
      if (selected) {
        previousSelections.push(selected.statisticCode);
      }
    }

    // Check if at least one required dependency is in previous selections (OR logic)
    return statistic.requiresAnyOf.some(required =>
      previousSelections.includes(required)
    );
  }
}
